import { CheckCheck, Trash2 } from "lucide-react";
import { BackButton, Button, Icon } from "@cntrl/ui";
import { sanitizeRoute } from "@cntrl/native";
import { useMobileBack } from "../../app/mobileBack.js";
import {
  useNotificationsInfinite,
  useMarkRead,
  useMarkAllRead,
  useDismiss,
  useDismissAll,
  type NotificationRow,
} from "../../app/hooks/useNotifications.js";
import { APP_ROUTE_ROOTS } from "../../app/deepLink.js";
import { useOpenNotificationRoute } from "../../app/notificationBand.js";
import { NotificationList } from "./NotificationList.js";
import styles from "./NotificationsScreen.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

export interface NotificationsScreenProps {
  /** Navigate to a notification's deep-link (already same-origin-sanitized). */
  onNavigate: (route: string) => void;
  /** Back to where the bell was tapped (the dashboard). */
  onBack: () => void;
}

/**
 * The notification center FULL PAGE ("Bell drawer (desktop) / full page (mobile)") — the
 * URL-addressable `/notifications` route (refresh / back / deep-link all work). Cursor-paginated (the drawer
 * shows only the first page); rows, per-row read/dismiss, mark-all / clear-all, and the designed
 * empty/loading/error states are the shared {@link NotificationList}. Person-scoped, all roles.
 */
export function NotificationsScreen({ onNavigate, onBack }: NotificationsScreenProps) {
  const list = useNotificationsInfinite();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const dismiss = useDismiss();
  const dismissAll = useDismissAll();

  const items = list.data?.pages.flatMap((p) => p.items ?? []) ?? [];

  // On mobile, back rides the shell header's leading slot (logo-replacement); the in-body back stays for
  // desktop, where there is no mobile header. Registering null on desktop leaves the header untouched.
  const isDesktop = !usePhoneLayout();
  useMobileBack(isDesktop ? null : onBack);

  const openRoute = useOpenNotificationRoute();

  function openRow(n: NotificationRow) {
    if (!n.read && n.id) markRead.mutate(n.id);
    if (n.route) {
      // Same-origin AND route-root allowlisted (parity with the push tap path) — off-list / cross-origin dropped.
      const safe = sanitizeRoute(n.route, APP_ROUTE_ROOTS);
      // A row for another of the person's bands asks before switching to it (notificationBand.ts).
      if (safe) openRoute(safe, n.bandId, onNavigate);
    }
  }

  return (
    <div className={styles.screen}>
      <header className={styles.header}>
        {isDesktop && <BackButton onClick={onBack} />}
        <h1 className={styles.title}>Notifications</h1>
        <div className={styles.headerActions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markAllRead.mutate()}
            disabled={items.length === 0}
          >
            <Icon icon={CheckCheck} size={16} /> Mark all read
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dismissAll.mutate()}
            disabled={items.length === 0}
          >
            <Icon icon={Trash2} size={16} /> Clear all
          </Button>
        </div>
      </header>

      <div className={styles.body}>
        <NotificationList
          items={items}
          isLoading={list.isLoading}
          isError={list.isError}
          error={list.error}
          onRetry={() => void list.refetch()}
          onOpen={openRow}
          onDismiss={(id) => dismiss.mutate(id)}
          hasMore={list.hasNextPage}
          isFetchingMore={list.isFetchingNextPage}
          onLoadMore={() => void list.fetchNextPage()}
        />
      </div>
    </div>
  );
}
